import React, { useEffect, useRef, useState } from 'react';
import profileApi from '../services/profileApi';
import Icon from './Icon';
import ProfilePdfComparison, { VERDICTS } from './ProfilePdfComparison';
import './ProfilePdfBatchUpload.css';

// The API reads at most this many PDFs per upload
const MAX_FILES = 20;

const isPdf = (file) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

// A row's choice: a contact id as a string, or 'new:panel' / 'new:current' for a new person
const NEW_PERSON = 'new:';
const isNewPerson = (choice) => typeof choice === 'string' && choice.startsWith(NEW_PERSON);

// One row per uploaded file: who it is matched to, their comparison, and what happened on saving
const toRow = (file, index) => {
  const reviews = {};
  [file.proposed, ...(file.alternatives || [])].filter(Boolean).forEach(review => {
    reviews[String(review.contact_id)] = review;
  });
  const suggested = Object.keys(reviews).map(Number);
  // Someone at another company who already has the PDF's LinkedIn profile
  const elsewhere = file.elsewhere || null;
  if (elsewhere) reviews[String(elsewhere.contact_id)] = elsewhere;
  Object.entries(file.new_person || {}).forEach(([where, review]) => {
    reviews[`${NEW_PERSON}${where}`] = review;
  });
  const proposed = file.proposed || elsewhere;
  // Outside a company (the upload lists candidates): with nobody on file it could be, adding them as a
  // new person at their employer is the likely choice. In a company's panel the user picks where.
  const onlyNew = Array.isArray(file.candidates) && !file.proposed && !elsewhere && file.candidates.length === 0
    && Boolean(file.new_person?.current);
  return {
    key: `${index}:${file.file_name}`,
    fileName: file.file_name || `File ${index + 1}`,
    importId: file.import_id || null,
    readError: file.error || null,
    pdfName: file.profile?.name || null,
    suggested,
    // Outside a company: everyone the PDF could be, with their company
    candidates: file.candidates || [],
    elsewhere,
    newPeople: file.new_person || {},
    reviews,
    choice: proposed ? String(proposed.contact_id) : onlyNew ? `${NEW_PERSON}current` : '',
    confirmed: false,
    skip: false,
    expanded: Boolean(file.proposed && file.proposed.verdict !== 'match'),
    loading: false,
    error: null,
    saved: false
  };
};

/**
 * Drop several LinkedIn profile PDFs, in a company's panel (each matched to someone with a record
 * at the company: target-list people and advisor connections) or, without companyId, anywhere
 * ("Add people from PDFs": matched against everyone, or added at the PDF's current employer). The
 * user checks each comparison, changes or skips a match, then saves them together. `embedded`
 * leaves out the section heading, for a dialog that has its own.
 */
const ProfilePdfBatchUpload = ({ companyId, companyName, onDataChanged, embedded = false }) => {
  const inputRef = useRef(null);
  // Uploaded but not saved: dropped when the panel closes or another company opens
  const pendingRef = useRef(new Set());
  const [phase, setPhase] = useState('idle'); // idle | reading | review | saving
  const [dragging, setDragging] = useState(false);
  const [people, setPeople] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const discardPending = () => {
    pendingRef.current.forEach(importId => profileApi.discardImport(importId));
    pendingRef.current.clear();
  };

  useEffect(() => {
    setPhase('idle');
    setPeople([]);
    setRows([]);
    setError(null);
    setSummary(null);
    const pending = pendingRef.current;
    return () => {
      pending.forEach(importId => profileApi.discardImport(importId));
      pending.clear();
    };
  }, [companyId]);

  const updateRow = (key, changes) => {
    setRows(previous => previous.map(row => (row.key === key ? { ...row, ...changes } : row)));
  };

  const readFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const pdfs = files.filter(isPdf);
    if (pdfs.length === 0) {
      setError('Choose LinkedIn profile PDFs (More > Save to PDF on a profile).');
      return;
    }
    if (pdfs.length > MAX_FILES) {
      setError(`Choose at most ${MAX_FILES} PDFs at a time.`);
      return;
    }
    discardPending();
    setError(files.length > pdfs.length ? `Left out ${files.length - pdfs.length} file(s) that aren't PDFs.` : null);
    setSummary(null);
    setPhase('reading');
    try {
      const result = companyId
        ? await profileApi.uploadCompanyPdfs(companyId, pdfs)
        : await profileApi.uploadPdfs(pdfs);
      result.files.forEach(file => {
        if (file.import_id) pendingRef.current.add(file.import_id);
      });
      setPeople(result.people || []);
      setRows(result.files.map(toRow));
      setPhase('review');
    } catch (err) {
      setError(err.message);
      setPhase('idle');
    }
  };

  const choosePerson = async (row, choice) => {
    updateRow(row.key, { choice, confirmed: false, error: null, expanded: true });
    if (!choice || row.reviews[choice]) return;
    updateRow(row.key, { loading: true });
    try {
      const review = await profileApi.reviewImport(row.importId, Number(choice));
      setRows(previous => previous.map(item => (item.key === row.key
        ? { ...item, loading: false, reviews: { ...item.reviews, [choice]: review } }
        : item)));
    } catch (err) {
      updateRow(row.key, { loading: false, error: err.message });
    }
  };

  // Two files for one person can't both be saved; new people are checked when saved
  const chosenCounts = rows.reduce((counts, row) => {
    if (row.choice && !isNewPerson(row.choice) && !row.skip && !row.readError && !row.saved) {
      counts[row.choice] = (counts[row.choice] || 0) + 1;
    }
    return counts;
  }, {});

  const isReady = (row) => {
    const review = row.choice ? row.reviews[row.choice] : null;
    return Boolean(!row.skip && !row.readError && !row.saved && !row.loading && review && !review.blocked &&
      review.changes?.length > 0 && (!review.needs_confirmation || row.confirmed) &&
      (isNewPerson(row.choice) || chosenCounts[row.choice] === 1));
  };
  const ready = rows.filter(isReady);
  const finished = rows.every(row => row.saved || row.readError || row.skip);

  const saveAll = async () => {
    setPhase('saving');
    setError(null);
    try {
      const result = await profileApi.saveImports(ready.map(row => (isNewPerson(row.choice)
        ? { importId: row.importId, newPerson: row.choice.slice(NEW_PERSON.length), planHash: row.reviews[row.choice].plan_hash }
        : {
          importId: row.importId,
          contactId: Number(row.choice),
          planHash: row.reviews[row.choice].plan_hash,
          confirmSamePerson: row.confirmed
        })));
      const outcomes = new Map(result.items.map(item => [item.import_id, item]));
      setRows(previous => previous.map(row => {
        const outcome = outcomes.get(row.importId);
        if (!outcome) return row;
        if (outcome.status === 'saved') {
          pendingRef.current.delete(row.importId);
          return { ...row, saved: true, created: Boolean(outcome.created), error: null, expanded: false };
        }
        // Refused with a fresh comparison (the person changed, or the PDF needs confirming): show it
        const reviews = outcome.review ? { ...row.reviews, [row.choice]: outcome.review } : row.reviews;
        return { ...row, reviews, confirmed: false, error: outcome.error, expanded: true };
      }));
      setSummary(`Saved ${result.saved} of ${ready.length} profile${ready.length === 1 ? '' : 's'}` +
        (result.failed ? `; ${result.failed} need${result.failed === 1 ? 's' : ''} another look.` : '.'));
      if (result.saved > 0 && onDataChanged) onDataChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setPhase('review');
    }
  };

  const clear = () => {
    discardPending();
    setRows([]);
    setPeople([]);
    setSummary(null);
    setError(null);
    setPhase('idle');
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (phase !== 'reading' && phase !== 'saving') readFiles(event.dataTransfer.files);
  };

  const personLabel = (person) => [person.name, person.title].filter(Boolean).join(' – ') +
    (companyId || !person.company ? '' : ` at ${person.company}`) +
    (person.on_target_list ? '' : ` (known by ${person.known_by.join(', ') || 'an advisor'})`);

  return (
    <div className={embedded ? 'pdf-batch pdf-batch--embedded' : 'company-section pdf-batch'}>
      {!embedded && <h3>LinkedIn PDFs</h3>}
      <div
        className={`pdf-batch__drop${dragging ? ' pdf-batch__drop--over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Icon name={phase === 'reading' ? 'loader' : 'upload'} spin={phase === 'reading'} size={20} />
        <div className="pdf-batch__drop-text">
          {phase === 'reading'
            ? 'Reading the PDFs…'
            : companyId
              ? <>Drop profile PDFs of people at {companyName || 'this company'} here, up to {MAX_FILES} at a time</>
              : <>Drop LinkedIn profile PDFs here, up to {MAX_FILES} at a time</>}
        </div>
        <button
          type="button"
          className="pdf-batch__choose"
          onClick={() => inputRef.current?.click()}
          disabled={phase === 'reading' || phase === 'saving'}
        >
          Choose PDFs
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          className="pdf-batch__input"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const files = event.target.files;
            readFiles(files);
            event.target.value = '';
          }}
        />
      </div>

      {error && <p className="pdf-batch__error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {summary && <p className="pdf-batch__summary" role="status"><Icon name="check" /> {summary}</p>}

      {rows.length > 0 && (
        <ul className="pdf-batch__rows">
          {rows.map(row => {
            const review = row.choice ? row.reviews[row.choice] : null;
            const verdict = review ? VERDICTS[review.verdict] || VERDICTS.likely : null;
            const duplicate = row.choice && !isNewPerson(row.choice) && chosenCounts[row.choice] > 1;
            const suggested = companyId
              ? people.filter(person => row.suggested.includes(person.contact_id))
              : row.candidates;
            const newPeople = Object.entries(row.newPeople);
            return (
              <li key={row.key} className={`pdf-batch__row${row.skip ? ' pdf-batch__row--skipped' : ''}${row.saved ? ' pdf-batch__row--saved' : ''}`}>
                <div className="pdf-batch__row-head">
                  <span className="pdf-batch__file"><Icon name="file-text" /> {row.fileName}</span>
                  {row.saved ? (
                    <span className="pdf-batch__badge pdf-batch__badge--good">
                      <Icon name="check" /> {row.created ? 'Added as a new person' : 'Saved'}
                    </span>
                  ) : !row.readError && (
                    <label className="pdf-batch__skip">
                      <input
                        type="checkbox"
                        checked={row.skip}
                        onChange={(event) => updateRow(row.key, { skip: event.target.checked })}
                        disabled={phase === 'saving'}
                      />
                      Skip
                    </label>
                  )}
                </div>

                {row.readError ? (
                  <p className="pdf-batch__row-error"><Icon name="alert-triangle" /> {row.readError}</p>
                ) : !row.skip && !row.saved && (
                  <>
                    {row.pdfName && <div className="pdf-batch__pdf-name">Profile of {row.pdfName}</div>}
                    <label className="pdf-batch__person">
                      <span>Person</span>
                      <select
                        value={row.choice || ''}
                        onChange={(event) => choosePerson(row, event.target.value)}
                        disabled={phase === 'saving'}
                      >
                        <option value="">
                          {suggested.length || row.elsewhere ? 'Choose a person…'
                            : companyId ? 'No one here by this name; choose…' : 'Nobody on file matches; choose…'}
                        </option>
                        {suggested.length > 0 && (
                          <optgroup label="Closest matches">
                            {suggested.map(person => (
                              <option key={`s${person.contact_id}`} value={person.contact_id}>{personLabel(person)}</option>
                            ))}
                          </optgroup>
                        )}
                        {row.elsewhere && (
                          <optgroup label="Already on file at another company">
                            <option value={String(row.elsewhere.contact_id)}>
                              {[row.elsewhere.contact_name, row.elsewhere.company && `at ${row.elsewhere.company}`].filter(Boolean).join(' ')}
                            </option>
                          </optgroup>
                        )}
                        {companyId && (
                          <optgroup label={`Everyone at ${companyName || 'this company'}`}>
                            {people.map(person => (
                              <option key={person.contact_id} value={person.contact_id}>{personLabel(person)}</option>
                            ))}
                          </optgroup>
                        )}
                        {newPeople.length > 0 && (
                          <optgroup label="Not on file yet">
                            {newPeople.map(([where, preview]) => (
                              <option key={where} value={`${NEW_PERSON}${where}`}>
                                Add as a new person at {preview.company.name}{preview.company.id ? '' : ' (a new company)'}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </label>
                    {!row.choice && newPeople.length > 0 && (
                      <div className="pdf-batch__muted">Nobody on file matches this PDF: pick someone, or add them as a new person.</div>
                    )}
                    {row.loading && <div className="pdf-batch__muted"><Icon name="loader" spin /> Comparing…</div>}
                    {duplicate && (
                      <p className="pdf-batch__row-error"><Icon name="alert-triangle" /> Another file is set to this person too; pick one.</p>
                    )}
                    {review && !row.loading && (
                      <details
                        className="pdf-batch__details"
                        open={row.expanded}
                        onToggle={(event) => updateRow(row.key, { expanded: event.currentTarget.open })}
                      >
                        <summary>
                          <span className={`pdf-batch__verdict pdf-batch__verdict--${verdict.tone}`}>
                            <Icon name={verdict.icon} /> {verdict.label}
                          </span>
                          <span className="pdf-batch__muted"> · {review.changes.length} change{review.changes.length === 1 ? '' : 's'}</span>
                        </summary>
                        <ProfilePdfComparison review={review} />
                      </details>
                    )}
                    {review?.needs_confirmation && !row.loading && (
                      <label className="pdf-batch__confirm">
                        <input
                          type="checkbox"
                          checked={row.confirmed}
                          onChange={(event) => updateRow(row.key, { confirmed: event.target.checked })}
                          disabled={phase === 'saving'}
                        />
                        Same person despite the differences
                      </label>
                    )}
                    {row.error && <p className="pdf-batch__row-error" role="alert"><Icon name="alert-triangle" /> {row.error}</p>}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="pdf-batch__actions">
          {!finished && (
            <button type="button" className="pdf-batch__save" onClick={saveAll} disabled={ready.length === 0 || phase === 'saving'}>
              <Icon name={phase === 'saving' ? 'loader' : 'check'} spin={phase === 'saving'} />
              {phase === 'saving' ? 'Saving…' : `Save ${ready.length} profile${ready.length === 1 ? '' : 's'}`}
            </button>
          )}
          <button type="button" className="pdf-batch__clear" onClick={clear} disabled={phase === 'saving'}>
            {finished ? 'Done' : 'Clear'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfilePdfBatchUpload;
